import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { RootState } from '~/redux/store';
import { useBuilding } from '~/hooks/building/useBuilding';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';

import GroupCard from '~/components/GroupCard/GroupCard';

import { toast } from 'react-toastify';
import { fetchGroups } from '~/api/groups';
import classes from './Groups.module.css';
import { GroupType } from '~/types/group';
import { updateGroup } from '~/api/group';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import { withFullScreenLoader } from '~/hocs/withFullScreenLoader';
import { CreateGroupFromValues } from '~/components/CreateGroupForm/CreateGroupForm';
import { getGroupsFromUserBuildings } from '~/actions/GroupActions';
import EditGroupDetails from '~/components/EditGroupDetails';
import GenericModal from '~/components/GenericModal/GenericModal';
import { useCobuDispatch } from '~/hooks/useCobuDispatch';
import { Tabs } from '~/components/Tabs';
import Spacer from '~/components/Spacer/Spacer';

type Props = ReturnType<typeof mapStateToProps>;
const Groups = () => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<GroupType[]>([]);
  const [groupType, setGroupType] = useState<string>('active');
  const [selectedGroup, setSelectedGroup] = useState<GroupType>();
  const { buildingId } = useParams<{ buildingId: string }>();
  const { data: selectedBuilding } = useBuilding(buildingId);
  const { loader } = useFullScreenLoader();

  const closeModal = () => {
    setSelectedGroup(undefined);
  };

  const doFetchGroups = async () => {
    try {
      loader.show();
      const res = await fetchGroups([buildingId]);
      const sortedData = res.data.sort(
        (a: any, b: any) => b.meta.groupMemberCount - a.meta.groupMemberCount
      );
      setGroups(sortedData);
      setSelectedGroup(undefined);
    } catch (error) {
      console.error(error);
      toast('There was an error fetching groups, please try again');
    }
    loader.hide();
  };

  const onUpdateGroup = (values: CreateGroupFromValues) => {
    if (!selectedGroup) return;

    loader.show();

    const group = selectedGroup;

    const createGroupFormData = new FormData();

    createGroupFormData.append('name', values.title.trim());
    createGroupFormData.append('building', values.building);
    createGroupFormData.append('description', values.description);
    createGroupFormData.append('active', values.active.toString());
    createGroupFormData.append('featured', values.featured.toString());
    createGroupFormData.append('isDefault', values.isDefault.toString());

    if (values.image?.path) {
      createGroupFormData.append('image', values.image);
    } else if (selectedGroup.imageUrl) {
      createGroupFormData.append('imageUrl', selectedGroup.imageUrl);
    }

    updateGroup(group.uuid, createGroupFormData).finally(() => {
      loader.hide();
      setSelectedGroup(undefined);
      doFetchGroups();
    });
  };

  useEffect(() => {
    doFetchGroups();
  }, []);

  const filteredGroups = useMemo(() => {
    const activeGroups = groups.filter((group) => {
      if (groupType === 'active') {
        return group.active;
      } else {
        return !group.active;
      }
    });

    return activeGroups;
  }, [groups, groupType]);

  return (
    <div className='content-container'>
      <div>
        <Breadcrumbs />

        <p className='h1-title'>
          <strong>{selectedBuilding?.name}</strong> Groups
        </p>
        <div style={{ width: '25%' }}>
          <Tabs.Container
            onChange={(value) => setGroupType(value)}
            value={groupType}
          >
            <Tabs.Option
              selected={groupType === 'active'}
              key={'active'}
              value={'active'}
            >
              Active Groups
            </Tabs.Option>
            <Tabs.Option
              selected={groupType === 'inActive'}
              key={'inActive'}
              value={'inActive'}
            >
              In-Active Groups
            </Tabs.Option>
          </Tabs.Container>
          <Spacer height={20} />
        </div>
        <div>
          <ul className={classes.groupList}>
            <li>
              <span>Groups ({filteredGroups.length})</span>
            </li>
          </ul>
        </div>
        <div className={classes.contentGroups}>
          {filteredGroups &&
            filteredGroups.map((group, index: number) => {
              return (
                <div className={classes.groupContainer} key={index}>
                  <GroupCard
                    key={group.uuid}
                    group={group}
                    onGroupSelected={setSelectedGroup}
                  />
                </div>
              );
            })}
        </div>

        <GenericModal
          title='EDIT GROUP DETAILS'
          content={
            <EditGroupDetails
              onConfirm={onUpdateGroup}
              groupSelected={selectedGroup}
              options={{
                hideBuildingField: true,
                hideInterestsField: true
              }}
              Action={
                <button type='submit' className='btn-standard btn-primary'>
                  Save Changes
                </button>
              }
            />
          }
          cancelOverride='Close'
          showModal={!!selectedGroup}
          onChange={closeModal}
          hideActionButton={true}
          actionButtonDisabled={true}
          enableOverflowScrolls={true}
        />
      </div>
    </div>
  );
};

function mapStateToProps(state: RootState) {
  return {
    currentUserRole: state.auth.userRole
  };
}

function mapDispatchToProps() {
  return {};
}

export default connect(
  mapStateToProps,
  mapDispatchToProps()
)(withFullScreenLoader(Groups));
